<template>
  <div>
    <div class="row">
      <div class="col-md-12 grid-margin">
        <div class="row">
          <div class="col-8 mb-0 mb-xl-0">
            <h4 class="mb-2">CLI API Keys</h4>
            <h5 class="font-weight-bold">
              Create keys for the ShortCodeAfrica CLI (`sca`)
            </h5>
          </div>
          <div class="col-4">
            <button
              class="btn btn-md btn-success text-white font-weight-bold float-right"
              @click="createKey"
            >
              New API Key
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-4">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="table-responsive" v-if="keys.length">
              <table class="table">
                <thead>
                  <tr>
                    <th class="key-col">Key</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in keys" :key="row.uuid">
                    <td class="key-col">
                      <span class="key-preview">{{ row.keyPreview || previewKey(row) }}</span>
                    </td>
                    <td>{{ formatDate(row.createdAt) }}</td>
                    <td class="text-right text-nowrap">
                      <button
                        class="btn btn-sm btn-outline-success mr-2"
                        @click="showKey(row)"
                      >
                        View / Copy
                      </button>
                      <button
                        class="btn btn-sm btn-outline-primary mr-2"
                        @click="rotateKey(row)"
                      >
                        Rotate
                      </button>
                      <button class="btn btn-sm btn-danger text-white" @click="revokeKey(row)">
                        Revoke
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="card card-inverse-info" v-else>
              <div class="card-body">
                <div class="card-title font-weight-bold">No API keys</div>
                <div class="card-text">
                  Create a key, then put it in <code>ussdappconfig.json</code> for the CLI.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="modal fade"
      id="viewApiKeyModal"
      tabindex="-1"
      role="dialog"
      aria-labelledby="viewApiKeyModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="viewApiKeyModalLabel">{{ viewModalTitle }}</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p class="text-muted" v-if="viewModalHint">{{ viewModalHint }}</p>
            <label class="col-form-label">API Key</label>
            <pre class="api-key-value">{{ viewedKey || "Key not available. Close and use View / Copy on the key in the table." }}</pre>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary text-white" :disabled="!viewedKey" @click="copyText(viewedKey)">
              Copy
            </button>
            <button type="button" class="btn btn-light" @click="closeViewModal">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import menuApi from "@/shared/menuApi";

export default {
  name: "ApiKeys",
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      keys: [],
      viewedKey: "",
      viewModalTitle: "API Key",
      viewModalHint: "",
    };
  },
  methods: {
    fullKey(row) {
      if (!row || typeof row === "string") {
        return "";
      }
      return row.cliKey || row.apiKey || row.keyPreview || row.keyPrefix || "";
    },
    previewKey(key) {
      const value = this.fullKey(key);
      if (!value) {
        return "••••";
      }
      if (value.length <= 16) {
        return value;
      }
      return value.slice(0, 16) + "…";
    },
    formatDate(value) {
      if (!value) {
        return "";
      }
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleString();
    },
    copyText(value) {
      if (!value) {
        this.toast.error("No API key to copy");
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(() => {
          this.toast.success("Copied to clipboard");
        }).catch(() => {
          this.fallbackCopy(value);
        });
        return;
      }
      this.fallbackCopy(value);
    },
    fallbackCopy(value) {
      const input = document.createElement("textarea");
      input.value = value;
      input.setAttribute("readonly", "");
      input.style.position = "absolute";
      input.style.left = "-9999px";
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      this.toast.success("Copied to clipboard");
    },
    showKey(key, options) {
      const opts = options || {};
      this.viewedKey = this.fullKey(key);
      this.viewModalTitle = opts.title || "API Key";
      this.viewModalHint = opts.hint || "Copy this key into ussdappconfig.json for the CLI.";
      this.$nextTick(() => {
        jQuery("#viewApiKeyModal").modal("show");
      });
    },
    closeViewModal() {
      jQuery("#viewApiKeyModal").modal("hide");
    },
    async createKey() {
      const response = await menuApi.doPost("/api/cli-keys", {});
      if (response.status === "00") {
        this.toast.success(response.message || "API key created");
        await this.getKeys();
        const createdKey = this.fullKey(response.data) || this.fullKey(this.keys[0]);
        this.showKey({ apiKey: createdKey }, {
          title: "API Key Created",
          hint: "Copy this key and save it in ussdappconfig.json.",
        });
      } else {
        this.toast.error(response.message || "Failed to create API key");
      }
    },
    async getKeys() {
      const response = await menuApi.doGet("/api/cli-keys");
      if (response.status === "00") {
        const list = Array.isArray(response.data) ? response.data : [];
        this.keys = list.map((row) => {
          const secret = (row && (row.cliKey || row.apiKey)) || "";
          const preview = row.keyPreview || (secret ? secret.slice(0, 16) + "…" : "");
          return Object.assign({}, row, { keyPreview: preview });
        });
      } else {
        this.toast.error(response.message || "Failed to load API keys");
      }
    },
    async rotateKey(key) {
      if (!confirm("Rotate this key? The current value will stop working.")) {
        return;
      }
      const response = await menuApi.doPost(`/api/cli-keys/${key.uuid}/rotate`, {});
      if (response.status === "00") {
        this.toast.success(response.message || "API key rotated");
        await this.getKeys();
        const rotatedKey = this.fullKey(response.data) || this.fullKey(this.keys.find((item) => item.uuid === key.uuid));
        this.showKey({ apiKey: rotatedKey }, {
          title: "API Key Rotated",
          hint: "Update ussdappconfig.json with this new key.",
        });
      } else {
        this.toast.error(response.message || "Failed to rotate API key");
      }
    },
    async revokeKey(key) {
      if (!confirm("Revoke this key? CLI access using it will stop immediately.")) {
        return;
      }
      const response = await menuApi.doPost(`/api/cli-keys/${key.uuid}/revoke`, {});
      if (response.status === "00") {
        this.toast.success(response.message || "API key revoked");
        this.getKeys();
      } else {
        this.toast.error(response.message || "Failed to revoke API key");
      }
    },
  },
  mounted() {
    if (!localStorage.getItem("token")) {
      this.$router.push("/login");
      return;
    }
    this.getKeys();
  },
};
</script>
<style scoped>
.key-col {
  width: 50%;
  min-width: 280px;
  max-width: 420px;
}
.key-preview {
  display: inline-block;
  min-width: 180px;
  color: #9ad0ff;
  font-family: Consolas, Monaco, monospace;
}
.api-key-value {
  background-color: #1e1e2f;
  color: #e9e6e6;
  border: 1px solid #424351;
  border-radius: 4px;
  padding: 12px;
  font-family: Consolas, Monaco, monospace;
  font-size: 14px;
  white-space: pre-wrap;
  word-break: break-all;
  min-height: 72px;
}
</style>
